/** @jsxRuntime classic */
/** @jsx jsx */
import React from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import schemaValidation from './schemaValidation'
import { useRouter } from 'next/router'
import API from '/repository/repoAPI'

import { jsx, Box, Text, Button } from 'theme-ui'
import ReactModal from 'react-modal'
import styles from './styles'
import FormSectionConsents from './components/FormSectionConsents'
import FormSectionUserInfo from './components/FormSectionUserInfo'
import RichTextRender from '/components/Commons/RichTextRender'
import redirectToPage from '/lib/redirect'
import userFormSubmissionDBO from '/utils/mappers/userFormSubmissionDBO'
import { ROUTE } from '/constants/pages'
import logger from '/infra/logger'
import { formatCamelCase } from '/utils/formatCamelCase'

const FormSection = ({ cmsContent, id, session, locale }) => {
  const [modalIsOpen, setIsOpen] = React.useState(false)
  const router = useRouter()

  // User can only participate once by campaing
  const findCampaingParticipation =
    session?.userDb?.eventParticipationHistory?.filter(
      ({ campaign_code }) => cmsContent?.campaignCode === campaign_code,
    )

  const userHasParticipated = session
    ? findCampaingParticipation?.length !== 0
    : false

  const { optionalQuestions } = cmsContent

  const optQuestions =
    optionalQuestions.length &&
    optionalQuestions.map(({ labelEnglishValue }) =>
      formatCamelCase(labelEnglishValue),
    )
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isDirty, touchedFields, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schemaValidation(optQuestions)),
    defaultValues: {
      gender: session?.user ? session?.user.gender : 'male',
      contact: 'call',
    },
  })

  const onSubmit = async (data) => {
    const getSelectedRegionData = cmsContent?.regionsOptions?.options?.filter(
      (option) => data.region === option.value.toLowerCase(),
    )

    const formatRegionData =
      getSelectedRegionData && Object.assign({}, ...getSelectedRegionData)

    const formSubmissionData = userFormSubmissionDBO(
      data,
      formatRegionData,
      session,
      optQuestions,
    )
    if (formSubmissionData && !userHasParticipated) {
      try {
        API.postEventForm('')({
          body: {
            locale: locale,
            campaignData: {
              campaign_code: cmsContent.campaignCode,
              event_name: cmsContent.eventName,
              event_start_date: cmsContent.eventStartDate,
              event_end_date: cmsContent.eventEndDate,
            },
            formData: formSubmissionData,
          },
        })
        setIsOpen(!modalIsOpen)
      } catch (error) {
        logger.error(JSON.stringify(error || {}))
      }
    }
  }

  const checkBoxValues = getValues([
    'consentCollectPersonalInformation',
    'consentMarketing',
    'consentAdvertising',
  ])

  const inputsValueChange = Object.values(touchedFields).includes(true)
  const hasChange = checkBoxValues.includes(true) || inputsValueChange

  React.useEffect(() => {
    hasChange && !session && redirectToPage(router, locale, ROUTE.LOGIN_PAGE)
  }, [hasChange])

  return (
    <React.Fragment>
      <Box id={id} sx={styles.mainContainer}>
        <Text sx={styles.mainContainer.openingText} as="p">
          {cmsContent?.termsAndConditions.participationText}
        </Text>
        <form
          id="event1-form"
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
          noValidate
        >
          <FormSectionConsents
            register={register}
            errors={errors}
            cmsErrorText={cmsContent?.requiredFormFieldError}
            cmsContent={cmsContent?.termsAndConditions}
          />
          <FormSectionUserInfo
            register={register}
            errors={errors}
            cmsContent={cmsContent}
            cmsErrorText={cmsContent?.requiredFormFieldError}
            session={session}
          />
          <Button
            disabled={!isDirty || !errors || !isValid || userHasParticipated}
          >
            {userHasParticipated
              ? cmsContent?.buttonTextParticipated
              : cmsContent?.buttonText}
          </Button>
        </form>
      </Box>
      <ReactModal
        style={styles.modal}
        isOpen={modalIsOpen}
        onRequestClose={() => setIsOpen(!modalIsOpen)}
        shouldCloseOnOverlayClick={true}
      >
        <RichTextRender htmlCode={cmsContent?.modalFormSubmission} />
        <Button
          sx={styles.modal.confirmationButton}
          onClick={() => redirectToPage(router, locale, ROUTE.HOME_PAGE)}
        >
          {userHasParticipated
            ? cmsContent?.modalButtonParticipated
            : cmsContent?.modalButtonParticipate}
        </Button>
      </ReactModal>
    </React.Fragment>
  )
}

export default FormSection
